/**
 * Collections Bar Chart Component
 * Shows collections by payment method over time
 */

import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Box, Typography, useTheme } from "@mui/material";
import type { CollectionsDataPoint } from "@/types/report";
import { formatCompactCurrency } from "@/utils/formatters";

interface CollectionsBarChartProps {
  data: CollectionsDataPoint[];
  height?: number;
  showLegend?: boolean;
  title?: string;
  stacked?: boolean;
}

const formatCurrency = (value: number): string => formatCompactCurrency(value);

const formatPeriod = (period: string): string => {
  // Handle both daily (YYYY-MM-DD) and monthly (YYYY-MM) formats
  if (period.length === 10) {
    // Daily format: YYYY-MM-DD -> "Dec 25"
    // Parse manually to avoid timezone issues (new Date("2024-12-25") is UTC midnight)
    const [year, month, day] = period.split("-").map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } else {
    // Monthly format: YYYY-MM -> "Dec '24"
    const [year, month] = period.split("-").map(Number);
    const date = new Date(year, month - 1, 1);
    return date.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
  }
};

interface TooltipPayloadItem {
  value: number;
  name: string;
  color: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const total = payload.reduce((sum: number, entry: TooltipPayloadItem) => sum + (entry.value || 0), 0);
    return (
      <Box
        sx={{
          bgcolor: "background.paper",
          p: 1.5,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          boxShadow: 2,
        }}
      >
        <Typography variant="subtitle2" gutterBottom sx={{
          fontWeight: 600
        }}>
          {label}
        </Typography>
        {payload.map((entry: TooltipPayloadItem, index: number) => (
          <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: 0.5,
                bgcolor: entry.color,
              }}
            />
            <Typography variant="body2">
              {entry.name}: {formatCurrency(entry.value)}
            </Typography>
          </Box>
        ))}
        <Box sx={{ mt: 1, pt: 1, borderTop: "1px solid", borderColor: "divider" }}>
          <Typography variant="body2" sx={{
            fontWeight: 600
          }}>
            Total: {formatCurrency(total)}
          </Typography>
        </Box>
      </Box>
    );
  }
  return null;
};

export const CollectionsBarChart: React.FC<CollectionsBarChartProps> = ({
  data,
  height = 300,
  showLegend = true,
  title,
  stacked = true,
}) => {
  const theme = useTheme();

  const chartData = data.map((dp) => ({
    period: dp.period,
    name: formatPeriod(dp.period),
    Cash: dp.cash_collected,
    Check: dp.check_collected,
    Card: dp.card_collected,
    ACH: dp.ach_collected,
    Other: dp.other_collected || 0,
  }));

  // Check if we have any "other" payments to show
  const hasOther = chartData.some((d) => d.Other > 0);
  // Check if only "other" exists (no known payment methods)
  const onlyOther = chartData.every((d) => d.Cash === 0 && d.Check === 0 && d.Card === 0 && d.ACH === 0);

  const colors = {
    Cash: theme.palette.chart.cash,
    Check: theme.palette.chart.check,
    Card: theme.palette.chart.card,
    ACH: theme.palette.chart.ach,
    Other: theme.palette.text.disabled,
  };

  const latestPoint = chartData[chartData.length - 1];
  const summaryText = latestPoint
    ? `Most recent period: ${latestPoint.name}, Cash: ${formatCurrency(latestPoint.Cash)}, Check: ${formatCurrency(latestPoint.Check)}, Card: ${formatCurrency(latestPoint.Card)}, ACH: ${formatCurrency(latestPoint.ACH)}`
    : "No collections data available";

  return (
    <Box role="figure" aria-label={title || "Collections Chart"}>
      {title && (
        <Typography variant="h6" gutterBottom sx={{
          fontWeight: 600
        }}>
          {title}
        </Typography>
      )}
      <Box className="sr-only" aria-live="polite">
        {summaryText}
      </Box>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={chartData}
          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
            axisLine={{ stroke: theme.palette.divider }}
            tickLine={{ stroke: theme.palette.divider }}
            interval="preserveStartEnd"
            minTickGap={50}
          />
          <YAxis
            tickFormatter={formatCurrency}
            tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
            axisLine={{ stroke: theme.palette.divider }}
            tickLine={{ stroke: theme.palette.divider }}
          />
          <Tooltip content={<CustomTooltip />} />
          {showLegend && <Legend />}
          {!onlyOther && (
            <Bar
              dataKey="Cash"
              stackId={stacked ? "a" : undefined}
              fill={colors.Cash}
              radius={stacked ? [0, 0, 0, 0] : [4, 4, 0, 0]}
            />
          )}
          {!onlyOther && (
            <Bar
              dataKey="Check"
              stackId={stacked ? "a" : undefined}
              fill={colors.Check}
              radius={stacked ? [0, 0, 0, 0] : [4, 4, 0, 0]}
            />
          )}
          {!onlyOther && (
            <Bar
              dataKey="Card"
              stackId={stacked ? "a" : undefined}
              fill={colors.Card}
              radius={stacked ? [0, 0, 0, 0] : [4, 4, 0, 0]}
            />
          )}
          {!onlyOther && (
            <Bar
              dataKey="ACH"
              stackId={stacked ? "a" : undefined}
              fill={colors.ACH}
              radius={hasOther ? [0, 0, 0, 0] : [4, 4, 0, 0]}
            />
          )}
          {hasOther && (
            <Bar
              dataKey="Other"
              name={onlyOther ? "Invoice Payments" : "Other"}
              stackId={stacked ? "a" : undefined}
              fill={colors.Other}
              radius={[4, 4, 0, 0]}
            />
          )}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default CollectionsBarChart;
